import { Button } from "../../components/ui/Button";
import { Card, CardHeader } from "../../components/ui/Card";
import { Pill } from "../../components/ui/Pill";
import type { Tone } from "../../lib/stream-status";
import { formatBytes } from "../../lib/format";
import { useUploads, type UploadStatus } from "./uploads";

const TONE: Record<UploadStatus, Tone> = { queued: "idle", uploading: "info", done: "ok", failed: "bad", cancelled: "idle" };

export function UploadQueue({ workspaceId }: { workspaceId: string }) {
  const { items, cancel, clearFinished } = useUploads();
  const mine = items.filter((x) => x.workspaceId === workspaceId);
  if (!mine.length) return null;
  const finished = mine.some((x) => x.status !== "queued" && x.status !== "uploading");

  return (
    <Card>
      <CardHeader title="Uploads" caption="keep this tab open until they finish"
        actions={finished && <Button size="sm" variant="ghost" onClick={clearFinished}>clear finished</Button>} />
      <ul className="flex flex-col">
        {mine.map((u) => {
          const pct = u.total ? Math.floor((u.sent / u.total) * 100) : 0;
          return (
            <li key={u.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-5 py-3 last:border-b-0">
              <span className="min-w-0 flex-1 truncate font-bold" title={u.name}>{u.name}</span>
              <Pill tone={TONE[u.status]} pulse={u.status === "uploading"}>{u.status}</Pill>
              {u.status === "uploading" && (
                <>
                  <progress max={100} value={pct} aria-label={`${u.name} upload progress`}
                    className="h-3 w-40 appearance-none overflow-hidden rounded-full border-2 border-ink [&::-moz-progress-bar]:rounded-full [&::-moz-progress-bar]:bg-ink [&::-webkit-progress-bar]:bg-paper [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-ink [&::-webkit-progress-value]:transition-[width] [&::-webkit-progress-value]:duration-500" />
                  <span className="text-muted tabular-nums">{pct}% of {formatBytes(u.total)}</span>
                </>
              )}
              {(u.status === "queued" || u.status === "uploading") && <Button size="sm" onClick={() => cancel(u.key)}>cancel</Button>}
              {u.error && <span role="alert" className="basis-full text-xs font-bold text-link-hover">{u.error}</span>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
